// Phase 9B.3b1b — SideDrawer-based shift editor.
//
// Replaces the modal ShiftForm with a context-preserving right drawer so
// planners can bulk-schedule without losing sight of the roster grid.
// Adds:
//   - "Opslaan & nog een toevoegen" (Save & add another) → keeps drawer open
//     and resets the form to a fresh empty shift.
//   - Inline conflict preview (calls POST /api/shifts/validate-preview on a
//     300ms debounce) — surfaces overlaps BEFORE the user hits save.
//   - Duplicate/delete actions preserved.
//
// This file consumes the exact same props signature as ShiftForm.jsx (drop-in)
// with two additional optional props: `onSaveAndAddAnother` and `apiPost`
// (defaults to `@/lib/api`.post — override for tests).
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Send, Trash2, Copy, PlusCircle, Search,
  Calendar as CalIcon, User as UserIcon, MapPin, Building2,
  Clock as ClockIcon, FileText, Repeat, Coffee, Tag, AlertTriangle,
} from "lucide-react";
import LocationScopeSelect from "@/components/LocationScopeSelect";
import SideDrawer from "@/components/rostr/SideDrawer";
import api from "@/lib/api";

// ---- Helpers -------------------------------------------------------------
function hoursBetween(start, end) {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  return Math.round((mins / 60) * 100) / 100;
}
function fmtDuration(h) {
  if (!h) return "—";
  const whole = Math.floor(h);
  const mins = Math.round((h - whole) * 60);
  return mins === 0 ? `${whole}u` : `${whole}u ${mins}m`;
}
function fmtDatePretty(iso, t) {
  if (!iso) return t("shift_date_missing") || "Kies een datum";
  try {
    return new Date(iso + "T12:00:00").toLocaleDateString("nl", {
      weekday: "long", day: "numeric", month: "long",
    });
  } catch { return iso; }
}

// ---- Atoms ---------------------------------------------------------------
function SectionHeader({ n, title, hint }) {
  return (
    <div className="flex items-center gap-2.5 mb-3">
      <span className="w-5 h-5 rounded-full grid place-items-center bg-[hsl(var(--primary))] text-white text-[10px] font-bold tabular-nums">{n}</span>
      <div className="min-w-0">
        <div className="font-display text-[13px] font-bold text-[hsl(var(--text-strong))] leading-none">{title}</div>
        {hint && <div className="text-[11px] text-[hsl(var(--text-muted))] mt-1 leading-tight">{hint}</div>}
      </div>
    </div>
  );
}
function Field({ label, icon: Icon, children, className = "", hint }) {
  return (
    <label className={`flex flex-col gap-1.5 ${className}`}>
      <span className="text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--text-muted))] flex items-center gap-1.5">
        {Icon && <Icon size={11} strokeWidth={2} />}
        {label}
      </span>
      {children}
      {hint && <span className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5">{hint}</span>}
    </label>
  );
}

// ---- Inline Conflict / Warning Preview ---------------------------------
// Phase 9B.3b2 — surfaces overlap + leave + unavailability BEFORE save.
function ConflictPreview({ conflicts, warnings, t }) {
  const relevantWarnings = (warnings || []).filter((w) =>
    w.code === "employee_on_leave" || w.code === "employee_unavailable"
  );
  if ((!conflicts || conflicts.length === 0) && relevantWarnings.length === 0) return null;
  const hasHardConflict = conflicts && conflicts.length > 0;
  const color = hasHardConflict ? "danger" : "warning";
  return (
    <div
      data-testid="shift-conflict-preview"
      className="rounded-xl border p-3 mb-4"
      style={{
        borderColor: `hsl(var(--${color}))`,
        background: `hsl(var(--${color}-soft))`,
      }}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" style={{ color: `hsl(var(--${color}))` }} />
        <div className="min-w-0 flex-1">
          <div className="text-[12px] font-bold" style={{ color: `hsl(var(--${color}))` }}>
            {hasHardConflict
              ? (t("shift_conflict_preview_title") || "Overlap gedetecteerd")
              : (t("shift_preview_warnings_title") || "Let op")}
          </div>
          <ul className="mt-1 space-y-0.5">
            {(conflicts || []).map((c, i) => (
              <li key={c.shift_id || i} className="text-[11.5px] text-[hsl(var(--text))]" data-testid="preview-overlap">
                <span className="tabular-nums font-semibold">{c.start_time}–{c.end_time}</span>
                {c.role_label ? ` · ${c.role_label}` : ""}
                {c.employee_name ? ` · ${c.employee_name}` : ""}
              </li>
            ))}
            {relevantWarnings.map((w, i) => (
              <li key={`w-${i}`} className="text-[11.5px] text-[hsl(var(--text))]"
                  data-testid={`preview-${w.code}`}>
                {w.code === "employee_on_leave" && (
                  <>
                    {(t("shift_preview_on_leave") || "Medewerker heeft goedgekeurd verlof")}
                    {w.start_date && w.end_date ? (
                      <span className="tabular-nums"> · {w.start_date} → {w.end_date}</span>
                    ) : null}
                  </>
                )}
                {w.code === "employee_unavailable" && (
                  <>
                    {(t("shift_preview_unavailable") || "Medewerker heeft aangegeven niet beschikbaar te zijn")}
                    {w.start_time && w.end_time ? (
                      <span className="tabular-nums"> · {w.start_time}–{w.end_time}</span>
                    ) : null}
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// ---- Employee Picker — searchable + prioritized for 100+ scale ------
// Prioritization (per rebuild spec §23–§25):
//   1. Currently selected employee (if any)
//   2. Available / in-scope / no conflict — visually calm, no status label
//   3. Overlap — small "Overlap 09:00–13:00" label
//   4. Unavailable — small "Niet beschikbaar" label
//   5. On leave — small "Afwezig" label
// Absent employees never get medical detail. Sickness reason is never shown.
function EmployeePicker({
  value, onChange, employees, allWeekShifts, form, editing, search, setSearch, t,
}) {
  const toMin = (h) => { const [a, b] = (h || "00:00").split(":").map(Number); return a * 60 + (b || 0); };
  const shiftDate = form?.date || "";
  const shiftS = toMin(form?.start_time);
  let shiftE = toMin(form?.end_time);
  if (shiftE <= shiftS) shiftE += 1440;

  // Build overlap-detection map from the visible week's shifts.
  const overlapByEmp = useMemo(() => {
    const m = new Map();
    if (!shiftDate) return m;
    for (const s of allWeekShifts) {
      if (!s.employee_id || s.date !== shiftDate) continue;
      if (editing && s._id === editing._id) continue;
      const sS = toMin(s.start_time);
      let sE = toMin(s.end_time);
      if (sE <= sS) sE += 1440;
      if (Math.max(shiftS, sS) < Math.min(shiftE, sE)) {
        m.set(s.employee_id, `${s.start_time}–${s.end_time}`);
      }
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allWeekShifts, shiftDate, form?.start_time, form?.end_time, editing?._id]);

  // Filter by search token.
  const q = (search || "").trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return employees;
    return employees.filter((e) => (e.name || "").toLowerCase().includes(q));
  }, [employees, q]);

  // Score: 0 = selected, 1 = available, 2 = overlap, 3 = other exception
  const scored = useMemo(() => {
    return filtered.map((e) => {
      let flag = null, score = 1;
      if (overlapByEmp.has(e._id)) { flag = { code: "overlap", info: overlapByEmp.get(e._id) }; score = 2; }
      if (e._id === value) score = 0;
      return { emp: e, score, flag };
    }).sort((a, b) => a.score - b.score || (a.emp.name || "").localeCompare(b.emp.name || ""));
  }, [filtered, overlapByEmp, value]);

  const selected = employees.find((e) => e._id === value);

  return (
    <div className="flex flex-col gap-2" data-testid="employee-picker">
      <div className="relative">
        <Search size={14} strokeWidth={2} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
        <input
          type="text"
          value={search}
          onChange={(ev) => setSearch(ev.target.value)}
          placeholder={t("employee_picker_search") || "Zoek medewerker…"}
          className="input-field !pl-9"
          data-testid="employee-picker-search"
          aria-label={t("employee_picker_search") || "Zoek medewerker"}
        />
      </div>
      <div
        className="max-h-[280px] overflow-y-auto rounded-xl border hairline divide-y divide-[hsl(var(--border))]"
        data-testid="employee-picker-list"
      >
        <button
          type="button"
          onClick={() => onChange("")}
          data-testid="employee-picker-unassigned"
          className={`w-full text-left px-3 py-2.5 text-[13px] flex items-center gap-2 hover:bg-[hsl(var(--muted))] ${value === "" ? "bg-[hsl(var(--primary)_/_0.08)] font-bold text-[hsl(var(--primary))]" : "font-semibold text-[hsl(var(--text-strong))]"}`}
        >
          <span className="w-2 h-2 rounded-full" style={{ background: value === "" ? "hsl(var(--warning))" : "hsl(var(--text-soft))" }} />
          {t("open_shift_label") || "Open dienst (geen medewerker)"}
        </button>
        {scored.length === 0 && (
          <div className="px-3 py-6 text-center text-[12px] text-[hsl(var(--text-muted))]" data-testid="employee-picker-empty">
            {t("employee_picker_no_results") || "Geen medewerker gevonden."}
          </div>
        )}
        {scored.map(({ emp, flag }) => {
          const isSelected = emp._id === value;
          return (
            <button
              key={emp._id}
              type="button"
              onClick={() => onChange(emp._id)}
              data-testid={`employee-picker-option-${emp._id}`}
              className={`w-full text-left px-3 py-2 text-[13px] flex items-center justify-between gap-3 hover:bg-[hsl(var(--muted))] transition-colors ${
                isSelected ? "bg-[hsl(var(--primary)_/_0.08)]" : ""
              }`}
            >
              <span className={`truncate ${isSelected ? "font-bold text-[hsl(var(--primary))]" : "font-medium text-[hsl(var(--text-strong))]"}`}>
                {emp.name}
              </span>
              {flag && flag.code === "overlap" && (
                <span
                  className="shrink-0 text-[10.5px] font-bold uppercase tracking-wider tabular-nums px-1.5 py-0.5 rounded-full"
                  style={{ background: "hsl(var(--warning-soft))", color: "hsl(var(--warning))" }}
                  title={`${t("overlap") || "Overlap"} ${flag.info}`}
                >
                  {(t("overlap") || "Overlap")} {flag.info}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {selected && (
        <div className="text-[11.5px] text-[hsl(var(--text-muted))]" data-testid="employee-picker-summary">
          {t("employee_selected") || "Gekozen"}:{" "}
          <span className="font-semibold text-[hsl(var(--text-strong))]">{selected.name}</span>
        </div>
      )}
    </div>
  );
}

// ---- Component -----------------------------------------------------------
export default function ShiftDrawer({
  open, editing, form, setForm,
  employees, locations, teams,
  allWeekShifts = [],
  isOwner = false, currentUser = null,
  repeatWeeks, setRepeatWeeks,
  onSave, onSaveAndAddAnother, onDelete, onDuplicate, onClose, t,
  apiPost,
}) {
  const [empSearch, setEmpSearch] = useState("");
  // Progressive disclosure (spec §32–§33): default form shows only WHEN,
  // WHO, WHERE. Team/role/pause/notes/repeat/status live behind "Meer
  // opties" and reveal automatically when editing a record that already
  // has non-default values in them.
  const hasAdvancedValue = Boolean(
    (form?.team_id) || (form?.role_label) || (form?.notes) ||
    (typeof form?.pause_minutes === "number") ||
    (repeatWeeks > 0) || (form?.status === "published")
  );
  const [showMore, setShowMore] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) setShowMore(!!editing && hasAdvancedValue); }, [open, editing?._id]);
  const post = apiPost || ((url, body) => api.post(url, body));

  const duration = hoursBetween(form?.start_time, form?.end_time);
  const emp = employees.find((e) => e._id === form?.employee_id);
  const loc = locations.find((l) => l._id === form?.location_id);

  const isScopedRole = !isOwner && (currentUser?.role === "manager" || currentUser?.role === "teamlead");
  const scopedIds = Array.isArray(currentUser?.managed_location_ids) ? currentUser.managed_location_ids : [];
  const visibleLocations = isScopedRole && scopedIds.length > 0
    ? locations.filter((l) => scopedIds.includes(l._id))
    : locations;
  const visibleTeams = form?.location_id
    ? teams.filter((tm) => !tm.location_id || tm.location_id === form.location_id)
    : teams;

  useEffect(() => {
    if (!open) return;
    if (!form?.location_id && visibleLocations.length === 1) {
      setForm({ ...form, location_id: visibleLocations[0]._id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, visibleLocations.length]);

  useEffect(() => {
    if (!open || !form?.team_id || !form?.location_id) return;
    const stillValid = visibleTeams.some((tm) => tm._id === form.team_id);
    if (!stillValid) {
      setForm({ ...form, team_id: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.location_id]);

  // ---- Inline conflict + warning preview (debounced) --------------------
  const [conflicts, setConflicts] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const seq = useRef(0);
  const canPreview = Boolean(
    form?.date && form?.start_time && form?.end_time && form?.employee_id
  );
  const previewKey = useMemo(
    () => `${form?.date}|${form?.start_time}|${form?.end_time}|${form?.employee_id}|${editing?._id || ""}`,
    [form?.date, form?.start_time, form?.end_time, form?.employee_id, editing?._id]
  );
  useEffect(() => {
    if (!open) return;
    if (!canPreview) { setConflicts([]); setWarnings([]); return; }
    const mine = ++seq.current;
    const handle = setTimeout(async () => {
      try {
        const { data } = await post("/shifts/validate-preview", {
          date: form.date,
          start_time: form.start_time,
          end_time: form.end_time,
          employee_id: form.employee_id,
          shift_id: editing?._id || null,
        });
        if (mine === seq.current) {
          setConflicts(data?.conflicts || []);
          setWarnings(data?.warnings || []);
        }
      } catch {
        if (mine === seq.current) { setConflicts([]); setWarnings([]); }
      }
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, previewKey]);

  const willPublish = form?.status === "published";
  const primaryLabel = willPublish
    ? (t("shift_publish_cta") || "Dienst publiceren")
    : (t("shift_save_cta") || "Dienst opslaan");

  const overlapHint = (duration > 24) ? (t("shift_max_length_error") || "Diensten mogen niet langer dan 24 uur zijn.") : null;
  const teamLocMismatch = (form?.location_id && form?.team_id && !visibleTeams.some((tm) => tm._id === form.team_id))
    ? (t("shift_team_loc_mismatch") || "Dit team hoort niet bij de gekozen locatie.")
    : null;

  const footer = (
    <>
      {editing && (
        <button onClick={onDelete} data-testid="shift-delete"
          className="btn btn-quiet !text-[hsl(var(--danger))] mr-auto">
          <Trash2 size={14} strokeWidth={2} />{t("delete")}
        </button>
      )}
      {editing && (
        <button onClick={onDuplicate} data-testid="shift-duplicate" className="btn btn-quiet">
          <Copy size={14} strokeWidth={2} />{t("duplicate_shift")}
        </button>
      )}
      <button onClick={onClose} className="btn btn-quiet" data-testid="shift-cancel">
        {t("cancel")}
      </button>
      {!editing && onSaveAndAddAnother && (
        <button
          onClick={onSaveAndAddAnother}
          data-testid="shift-save-add-another"
          className="btn btn-ghost"
          title={t("shift_save_add_another_hint") || "Bewaar deze dienst en houd de drawer open om er nog een toe te voegen"}
        >
          <PlusCircle size={14} strokeWidth={2} />
          {t("shift_save_add_another") || "Opslaan & nog een"}
        </button>
      )}
      <button onClick={onSave} data-testid="shift-save" className="btn btn-primary">
        <Send size={14} strokeWidth={2} /> {primaryLabel}
      </button>
    </>
  );

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      testid="shift-drawer"
      title={editing ? t("edit_shift") : t("add_shift")}
      subtitle={editing ? t("edit") : t("new")}
      width="lg"
      footer={footer}
    >
      {/* Live summary strip */}
      <div
        className="mb-4 p-3.5 rounded-2xl"
        style={{
          background: "linear-gradient(135deg, hsl(var(--primary-soft)) 0%, hsl(var(--surface)) 100%)",
          border: "1px solid hsl(var(--border))",
        }}
        data-testid="shift-live-summary"
      >
        <div className="text-[10.5px] font-semibold uppercase tracking-wider text-[hsl(var(--primary))] mb-1">
          {t("shift_preview_label")}
        </div>
        <div className="font-display text-[15px] font-bold text-[hsl(var(--text-strong))] tabular-nums leading-snug">
          {fmtDatePretty(form?.date, t)}
          {(form?.start_time && form?.end_time) && (<> · {form.start_time}–{form.end_time}</>)}
        </div>
        <div className="text-[12px] text-[hsl(var(--text-muted))] mt-1 flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-[hsl(var(--text))] tabular-nums" data-testid="shift-duration">
            {fmtDuration(duration)}
          </span>
          <span>·</span>
          <span>{emp ? emp.name : t("shift_unassigned_label")}</span>
          {loc && (<><span>·</span><span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full" style={{ background: loc.color || "hsl(var(--primary))" }} />
            {loc.name}
          </span></>)}
        </div>
      </div>

      {/* Inline conflict banner — before body sections so it's the first thing seen */}
      <ConflictPreview conflicts={conflicts} warnings={warnings} t={t} />

      {/* 1 — WANNEER */}
      <section data-testid="shift-section-when" className="mb-5">
        <SectionHeader n="1" title={t("shift_section_when")} hint={t("shift_section_when_hint")} />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field label={t("date")} icon={CalIcon}>
            <input type="date" data-testid="shift-date" value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-field" />
          </Field>
          <Field label={t("start")} icon={ClockIcon}>
            <input type="time" data-testid="shift-start" value={form.start_time}
              onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="input-field" />
          </Field>
          <Field label={t("end")} icon={ClockIcon}>
            <input type="time" data-testid="shift-end" value={form.end_time}
              onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="input-field" />
          </Field>
        </div>
        {overlapHint && (
          <div className="mt-2 text-[11.5px] text-[hsl(var(--danger))] font-medium" data-testid="shift-warn-length">
            {overlapHint}
          </div>
        )}
      </section>

      {/* 2 — WIE — searchable prioritized picker for 100+ employees */}
      <section data-testid="shift-section-who" className="mb-5">
        <SectionHeader n="2" title={t("shift_section_who")} hint={t("shift_section_who_hint")} />
        <EmployeePicker
          value={form.employee_id}
          onChange={(id) => setForm({ ...form, employee_id: id })}
          employees={employees}
          allWeekShifts={allWeekShifts}
          form={form}
          editing={editing}
          search={empSearch}
          setSearch={setEmpSearch}
          t={t}
        />
      </section>

      {/* 3 — WAAR — location only in default view; team moved to Meer opties */}
      <section data-testid="shift-section-where" className="mb-5">
        <SectionHeader n="3" title={t("shift_section_where")} hint={t("shift_section_where_hint")} />
        {visibleLocations.length <= 1 && form?.location_id ? (
          <div
            data-testid="shift-location-single"
            className="inline-flex items-center gap-2 h-10 px-3 rounded-xl border hairline bg-[hsl(var(--muted))] text-[13px] font-semibold text-[hsl(var(--text-strong))]"
          >
            <span className="w-2 h-2 rounded-full" style={{ background: loc?.color || "hsl(var(--primary))" }} />
            {loc?.name || "—"}
          </div>
        ) : (
          <Field label={t("location")} icon={MapPin}>
            <LocationScopeSelect
              variant="input"
              allowAll={false}
              locations={visibleLocations}
              value={form.location_id}
              onChange={(v) => setForm({ ...form, location_id: v })}
              testid="shift-location"
              ariaLabel={t("location")}
            />
          </Field>
        )}
      </section>

      {/* --- Progressive disclosure divider --- */}
      <button
        type="button"
        onClick={() => setShowMore((v) => !v)}
        data-testid="shift-toggle-more"
        aria-expanded={showMore}
        className="mb-4 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[hsl(var(--primary))] hover:underline"
      >
        {showMore ? (t("shift_hide_more") || "Minder opties") : (t("shift_show_more") || "Meer opties")}
        <span aria-hidden="true" className={`transition-transform ${showMore ? "rotate-180" : ""}`}>▾</span>
      </button>

      {showMore && (<>
      {/* 4 — DETAILS */}
      <section data-testid="shift-section-details" className="mb-5">
        <SectionHeader n="4" title={t("shift_section_details")} hint={t("shift_section_details_hint")} />
        {visibleTeams.length > 0 && (
          <div className="mb-3">
            <Field label={t("team")} icon={Building2}>
              <select data-testid="shift-team" value={form.team_id}
                onChange={(e) => setForm({ ...form, team_id: e.target.value })} className="input-field">
                <option value="">—</option>
                {visibleTeams.map((tm) => (<option key={tm._id} value={tm._id}>{tm.name}</option>))}
              </select>
            </Field>
            {teamLocMismatch && (
              <div className="mt-2 text-[11.5px] text-[hsl(var(--danger))] font-medium" data-testid="shift-warn-teamloc">
                {teamLocMismatch}
              </div>
            )}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label={t("role_label")} icon={Tag}>
            <input data-testid="shift-role" value={form.role_label || ""}
              onChange={(e) => setForm({ ...form, role_label: e.target.value })} className="input-field"
              placeholder={t("shift_role_placeholder")} />
          </Field>
          <Field label={t("shift_pause") || "Pauze (min)"} icon={Coffee}
            hint={t("shift_pause_hint") || "Leeg = automatisch berekend uit instellingen"}>
            <input
              type="number" min="0" step="5"
              data-testid="shift-pause"
              value={form.pause_minutes === null || form.pause_minutes === undefined ? "" : form.pause_minutes}
              onChange={(e) => {
                const v = e.target.value;
                setForm({ ...form, pause_minutes: v === "" ? null : Number(v) });
              }}
              placeholder={t("shift_pause_auto") || "auto"}
              className="input-field tabular-nums"
            />
          </Field>
        </div>
        <div className="mt-3">
          <Field label={t("shift_notes")} icon={FileText}>
            <textarea data-testid="shift-notes" value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="input-field !h-auto py-2.5 resize-none"
              placeholder={t("shift_notes_placeholder")} />
          </Field>
        </div>

        {!editing && (
          <div className="mt-3">
            <Field label={t("repeat_label")} icon={Repeat}>
              <div className="flex items-center gap-3 p-3 rounded-2xl border hairline bg-[hsl(var(--muted)_/_0.4)]">
                <button
                  type="button" role="switch"
                  aria-checked={repeatWeeks > 0}
                  onClick={() => setRepeatWeeks(repeatWeeks > 0 ? 0 : 3)}
                  data-testid="shift-repeat-toggle"
                  className={`relative shrink-0 h-7 w-12 rounded-full transition-colors ${
                    repeatWeeks > 0 ? "bg-[hsl(var(--primary))]" : "bg-white border hairline"
                  }`}
                >
                  <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.2)] transition-all ${
                    repeatWeeks > 0 ? "right-1" : "left-1"
                  }`} />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))]">{t("repeat_weekly")}</div>
                  <div className="text-[11px] text-[hsl(var(--text-muted))]">{t("repeat_hint")}</div>
                </div>
                {repeatWeeks > 0 && (
                  <select value={repeatWeeks} onChange={(e) => setRepeatWeeks(Number(e.target.value))}
                    data-testid="shift-repeat-count"
                    className="h-9 px-3 rounded-xl bg-white border hairline text-[13px] font-semibold tabular-nums">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 10, 12, 16, 20, 26].map((n) => (
                      <option key={n} value={n}>+{n} {t("weeks_short")}</option>
                    ))}
                  </select>
                )}
              </div>
            </Field>
          </div>
        )}
      </section>

      </>)}
    </SideDrawer>
  );
}
